import { useCallback, useContext } from "react";

import { StateContext } from "../../context/globalContext/context";
import { iGlobalContext } from "../../context/globalContext/interface";
import { getChatId } from "../../utils/chat/chatId";
import { useIntentEventFirer } from "./postIntentEvent";

/**
 * Thin wrapper over useIntentEventFirer that auto-injects three cross-cutting
 * fields on the way out — chatId, itineraryIndex, ownerEmail — when they are
 * not already in the caller-supplied custom payload. Caller-supplied values
 * always win and are never overridden.
 *
 *   chatId         ← localStorage `_zenvoya_chat_id`
 *   itineraryIndex ← 1-based position of `custom.itineraryId` in chatData
 *                    (only when itineraryId is present in custom)
 *   ownerEmail     ← StateContext userInfo.email
 *
 * Use this in preference to raw useIntentEventFirer for any Theresias proposal
 * event so the Traveler-Graph cross-cutting fields stay consistent without
 * each call site repeating the lookup.
 */
export function useTheresiasEventBridge(): (
  eventName: string,
  custom?: Record<string, unknown>
) => void {
  const fireIntent = useIntentEventFirer();
  const { chatData, userInfo } = useContext(StateContext) as iGlobalContext;

  return useCallback(
    (eventName: string, custom?: Record<string, unknown>) => {
      const enriched: Record<string, unknown> = { ...(custom ?? {}) };

      if (!("chatId" in enriched)) {
        try {
          const chatId = getChatId();
          if (chatId) enriched.chatId = chatId;
        } catch {
          /* localStorage unavailable — skip silently */
        }
      }

      if ("itineraryId" in enriched && !("itineraryIndex" in enriched)) {
        const target = String(enriched.itineraryId);
        const idx = Array.isArray(chatData)
          ? chatData.findIndex((d: { id?: string }) => d?.id === target)
          : -1;
        if (idx >= 0) enriched.itineraryIndex = idx + 1;
      }

      if (!("ownerEmail" in enriched) && userInfo?.email) {
        enriched.ownerEmail = userInfo.email;
      }

      fireIntent(eventName, enriched);
    },
    [chatData, userInfo?.email, fireIntent]
  );
}
